// Das Material einer Besichtigung – die reine Rechenlogik, ohne React
// (PLAN.md, E3, S3 und „Handwerk“; Mockup /crm/freigabe/:id, rechte Spalte).
//
// Die Materialliste entsteht aus drei Gruppen, in dieser Reihenfolge:
//   1. „Standard – immer dabei“: die aktiven Einträge der Standardliste mit
//      Art „immer“ (vorbelegt, Menge änderbar, entfernbar);
//   2. „Aus der Aufnahme“: je Station und Längen-Art EINE Position mit der
//      Summe der Meter (Kabelkanal, Rohr, Kabel – daten/laengen-arten.json);
//      seit E3b (O65) dazu das Material, das der Verkäufer draußen angegeben
//      hat – je Angabe eine Position mit ihrer Station, nach den Längen der
//      Station; was er am Schluss (Seite „Material“) angab, zuletzt. Menge 0
//      („doch nicht gebraucht“) ergibt keine Position;
//   3. „Vom Büro ergänzt“: aus der Auswahl (Einträge mit Art „auswahl“) oder
//      frei getippt.
// Positionen sind Kopien, keine Verweise: Ändert das Büro später die
// Standardliste, bleibt die Materialliste, wie sie freigegeben wurde.
//
// Der Entwurf lebt bis zum Freigeben NUR im Browser des Büros
// (localStorage, je Besichtigung ein Schlüssel) – er überlebt ein Neuladen,
// und ein zweites Fenster („Material in eigenem Fenster“) liest denselben.
// Am Server gibt es Positionen erst mit der Freigabe. Die Mengen stehen im
// Entwurf so, wie getippt („12,5“): Halb getippte Zahlen („12,“) gehen beim
// Speichern nicht verloren, und beide Fenster zeigen dasselbe. Zur Zahl
// werden sie erst beim Senden – geprüft mit demselben Vertrag wie am Server
// (Kern, freigabeEingabe).
//
// Nach der Freigabe ändert das Büro das Material mit derselben Liste
// („Material ändern“): Sie beginnt mit den geltenden Positionen (ihre
// Kennungen bleiben), der Server ersetzt, was sich geändert hat, und nennt
// die Vorgängerin (Gernots Entscheidung 5). Auch diese Änderung liegt bis
// zum Speichern im Browser – mit der Grundlage, auf der sie begann: Hat
// inzwischen jemand anderer das Material geändert, gilt sie nicht mehr.

import {
  aufZweiStellen,
  BUERO_GRENZEN,
  freigabeEingabe,
  istKennung,
  LAENGEN_ARTEN,
  MATERIAL_HERKUENFTE,
  neueKennung,
  STATIONEN,
  type CrmAufnahme,
  type FreigabeEingabe,
  type LaengenArt,
  type MaterialAmServer,
  type MaterialHerkunft,
  type PositionAmServer,
  type StandardmaterialZeile,
} from "@happy-energy/kern";

/** Eine Position im Entwurf – wie am Server, nur die Menge als getippter Text. */
export interface EntwurfPosition {
  id: string;
  herkunft: MaterialHerkunft;
  /** Bei Positionen aus der Aufnahme: die Station der Längen. */
  stationId: string | null;
  artikelnummer: string | null;
  bezeichnung: string;
  /** So, wie getippt: „2“, „12,5“ – erst beim Senden eine Zahl. */
  menge: string;
  einheit: string;
  /** Noch ohne Funktion (DECISIONS.md, 12.10.) – bleibt, wie es kam. */
  preis: number | null;
}

/** „freigabe“: vor der Freigabe; „aenderung“: Material nach der Freigabe ändern. */
export type EntwurfArt = "freigabe" | "aenderung";

export interface Entwurf {
  /** Fassung des Formats – ändert es sich, fällt ein alter Entwurf auf die Vorbelegung zurück. */
  fassung: 1;
  aufnahmeId: string;
  art: EntwurfArt;
  /**
   * Worauf der Entwurf aufbaut. Bei „freigabe“: der Fingerabdruck dessen, was
   * aus der Aufnahme kommt (Längen, seit E3b auch Material), beim Anlegen –
   * ändert ein Nachtrag es, sagt die Seite es und bietet „Aus der Aufnahme
   * neu übernehmen“ an. Bei „aenderung“: die Kennungen
   * der geltenden Positionen beim Anlegen – passt das nicht mehr, hat jemand
   * anderer inzwischen geändert, und der Entwurf gilt nicht mehr.
   */
  grundlage: string;
  positionen: EntwurfPosition[];
}

/** Die Gruppen der Liste, in dieser Reihenfolge – auch beim Senden. */
export const GRUPPEN: readonly { herkunft: MaterialHerkunft; titel: string }[] = [
  { herkunft: "standard", titel: "Standard – bei jeder Anlage dabei" },
  { herkunft: "aufnahme", titel: "Aus der Aufnahme" },
  { herkunft: "buero", titel: "Vom Büro ergänzt" },
];

const FASSUNG = 1;

/** Woher neue Kennungen kommen – in den Tests feste, sonst UUID aus dem Kern. */
export type KennungsQuelle = () => string;

// --- Mengen ----------------------------------------------------------------------------

/**
 * Liest eine Menge, wie Menschen sie tippen: „2“, „12,5“ oder „12.5“, mit
 * höchstens zwei Nachkommastellen. Leer oder ungültig: null.
 */
export function mengeLesen(text: string): number | null {
  const roh = text.trim().replace(",", ".");
  if (!/^\d{1,5}(\.\d{1,2})?$/.test(roh)) return null;
  const zahl = Number(roh);
  return Number.isFinite(zahl) && zahl >= 0 && zahl <= BUERO_GRENZEN.menge ? zahl : null;
}

/** Eine Menge zum Anzeigen und Tippen: 12.5 → „12,5“, 3.3000000000000003 → „3,3“. */
export function mengeZeigen(zahl: number): string {
  return String(aufZweiStellen(zahl)).replace(".", ",");
}

// --- Die drei Gruppen ----------------------------------------------------------------------

/** „Standard – immer dabei“: nur aktive Einträge mit Art „immer“, in der Reihenfolge der Liste. */
export function standardPositionen(eintraege: readonly StandardmaterialZeile[], kennung: KennungsQuelle = neueKennung): EntwurfPosition[] {
  return eintraege
    .filter((e) => e.aktiv && e.art === "immer")
    .map((e) => ({
      id: kennung(),
      herkunft: "standard",
      stationId: null,
      artikelnummer: e.artikelnummer,
      bezeichnung: e.bezeichnung,
      menge: mengeZeigen(e.menge),
      einheit: e.einheit,
      preis: null,
    }));
}

/**
 * Was „Aus der Aufnahme“ braucht: Stationen, Längen und (seit E3b) das
 * Material von draußen. `material` darf fehlen – eine Antwort ohne die Liste
 * (Server mit altem Stand) heißt: keines.
 */
export type AufnahmeLaengen = Pick<CrmAufnahme, "stationen" | "laengen"> & { material?: readonly MaterialAmServer[] };

interface LaengenSumme {
  stationId: string;
  stationNr: number;
  art: LaengenArt;
  meter: number;
}

/**
 * Je Station (nach Nummer) und Längen-Art (in der Reihenfolge der
 * Datenliste) die Summe der Meter – nur, wo mehr als 0 m herauskommt.
 * Gerundet wie am Server (zwei Stellen), damit 1,1 + 2,2 nicht 3,3000…01 ist.
 */
function laengenSummen(aufnahme: AufnahmeLaengen): LaengenSumme[] {
  const summen: LaengenSumme[] = [];
  for (const s of [...aufnahme.stationen].sort((a, b) => a.nr - b.nr)) {
    for (const art of LAENGEN_ARTEN) {
      const meter = aufnahme.laengen.filter((l) => l.stationId === s.id && l.laengenArt === art.schluessel).reduce((summe, l) => summe + l.meter, 0);
      const gerundet = aufZweiStellen(meter);
      if (gerundet > 0) summen.push({ stationId: s.id, stationNr: s.nr, art, meter: gerundet });
    }
  }
  return summen;
}

/**
 * Das Material von draußen, das in die Liste gehört (seit E3b): Menge über 0,
 * nach Station (die Seite „Material“ zuletzt), darin in der Reihenfolge des
 * Servers.
 */
function materialVonDraussen(aufnahme: AufnahmeLaengen): MaterialAmServer[] {
  const nr = new Map(aufnahme.stationen.map((s) => [s.id, s.nr]));
  const rang = (m: MaterialAmServer) => (m.stationId === null ? Number.MAX_SAFE_INTEGER : (nr.get(m.stationId) ?? Number.MAX_SAFE_INTEGER - 1));
  return (aufnahme.material ?? [])
    .map((m, i) => ({ m, i }))
    .filter(({ m }) => aufZweiStellen(m.menge) > 0)
    .sort((a, b) => rang(a.m) - rang(b.m) || a.i - b.i)
    .map(({ m }) => m);
}

/**
 * „Aus der Aufnahme“: je Station die Längen (je Art eine Position mit der
 * Summe der Meter), dann das Material von dort; zuletzt das Material vom
 * Schluss. So steht alles einer Station beisammen.
 */
export function aufnahmePositionen(aufnahme: AufnahmeLaengen, kennung: KennungsQuelle = neueKennung): EntwurfPosition[] {
  const laengen = laengenSummen(aufnahme);
  const material = materialVonDraussen(aufnahme);
  const ausMaterial = (m: MaterialAmServer): EntwurfPosition => ({
    id: kennung(),
    herkunft: "aufnahme",
    stationId: m.stationId,
    artikelnummer: m.artikelnummer,
    bezeichnung: m.bezeichnung,
    menge: mengeZeigen(m.menge),
    einheit: m.einheit,
    preis: null,
  });
  const stationen = [...aufnahme.stationen].sort((a, b) => a.nr - b.nr);
  return [
    ...stationen.flatMap((st) => [
      ...laengen
        .filter((s) => s.stationId === st.id)
        .map(
          (s): EntwurfPosition => ({
            id: kennung(),
            herkunft: "aufnahme",
            stationId: s.stationId,
            artikelnummer: null,
            bezeichnung: s.art.name,
            menge: mengeZeigen(s.meter),
            einheit: s.art.einheit,
            preis: null,
          }),
        ),
      ...material.filter((m) => m.stationId === st.id).map(ausMaterial),
    ]),
    // Am Schluss angegeben – oder an einer Station, die die Besichtigung nicht (mehr) kennt: nichts geht verloren.
    ...material.filter((m) => m.stationId === null || !stationen.some((st) => st.id === m.stationId)).map(ausMaterial),
  ];
}

/**
 * Der Fingerabdruck dessen, was aus der Aufnahme kommt: ändert ein Nachtrag
 * eine Länge oder (seit E3b) eine Menge, kommt Material dazu, ändert er sich.
 * Ohne Material genau wie vor E3b – sonst zeigte jeder offene Entwurf nach
 * dem Update grundlos „neu übernehmen“ (Prüfung des Plans E3b).
 */
export function aufnahmeFingerabdruck(aufnahme: AufnahmeLaengen): string {
  const laengen = laengenSummen(aufnahme)
    .map((s) => `${s.stationId}:${s.art.schluessel}:${s.meter}`)
    .join("|");
  const material = materialVonDraussen(aufnahme);
  if (material.length === 0) return laengen;
  return `${laengen}#${material.map((m) => `${m.id}:${aufZweiStellen(m.menge)}`).join("|")}`;
}

/** Ein Eintrag „aus der Auswahl“: mit seiner Menge und Einheit angefügt. */
export function auswahlPosition(eintrag: StandardmaterialZeile, kennung: KennungsQuelle = neueKennung): EntwurfPosition {
  return {
    id: kennung(),
    herkunft: "buero",
    stationId: null,
    artikelnummer: eintrag.artikelnummer,
    bezeichnung: eintrag.bezeichnung,
    menge: mengeZeigen(eintrag.menge),
    einheit: eintrag.einheit,
    preis: null,
  };
}

/** Was das Büro frei tippt. */
export interface FreieEingabe {
  bezeichnung: string;
  menge: string;
  einheit: string;
}

/** Prüft eine freie Position, bevor sie dazukommt – null: passt; sonst, was fehlt. */
export function freieEingabePruefen(e: FreieEingabe): string | null {
  if (!e.bezeichnung.trim()) return "Bitte eine Bezeichnung eingeben.";
  if (e.bezeichnung.trim().length > BUERO_GRENZEN.bezeichnung) return `Die Bezeichnung hat höchstens ${BUERO_GRENZEN.bezeichnung} Zeichen.`;
  if (mengeLesen(e.menge) === null) return "Bitte eine Menge eingeben, zum Beispiel 2 oder 12,5.";
  if (!e.einheit.trim()) return "Bitte eine Einheit eingeben (z. B. Stk, m).";
  if (e.einheit.trim().length > BUERO_GRENZEN.einheit) return `Die Einheit hat höchstens ${BUERO_GRENZEN.einheit} Zeichen.`;
  return null;
}

/** Eine frei getippte Position (vorher `freieEingabePruefen`). */
export function freiePosition(e: FreieEingabe, kennung: KennungsQuelle = neueKennung): EntwurfPosition {
  return {
    id: kennung(),
    herkunft: "buero",
    stationId: null,
    artikelnummer: null,
    bezeichnung: e.bezeichnung.trim(),
    menge: e.menge.trim(),
    einheit: e.einheit.trim(),
    preis: null,
  };
}

// --- Vorbelegung und Änderung ------------------------------------------------------------------

/**
 * Die Vorbelegung vor der Freigabe: Standard + Aufnahme. „Vom Büro
 * ergänzt“ beginnt leer. Wird erst gespeichert, wenn das Büro etwas ändert –
 * bis dahin zeigt die Seite immer die frische Vorbelegung (eine neue
 * Standardliste, ein Nachtrag sind gleich drin).
 */
export function vorbelegung(
  aufnahmeId: string,
  standard: readonly StandardmaterialZeile[],
  aufnahme: AufnahmeLaengen,
  kennung: KennungsQuelle = neueKennung,
): Entwurf {
  return {
    fassung: FASSUNG,
    aufnahmeId,
    art: "freigabe",
    grundlage: aufnahmeFingerabdruck(aufnahme),
    positionen: [...standardPositionen(standard, kennung), ...aufnahmePositionen(aufnahme, kennung)],
  };
}

/** Die geltenden Positionen (nicht ersetzt, nicht entfernt) – die Materialliste, wie sie gilt. */
export function geltendePositionen(positionen: readonly PositionAmServer[]): PositionAmServer[] {
  return positionen.filter((p) => p.ersetztAm === null);
}

/** Die Grundlage einer Änderung: die Kennungen der geltenden Positionen. */
export function geltendeGrundlage(positionen: readonly PositionAmServer[]): string {
  return geltendePositionen(positionen)
    .map((p) => p.id)
    .sort()
    .join(",");
}

/**
 * Die Grundlage einer Änderung als Liste – die Seite schickt sie mit, der
 * Server vergleicht sie mit dem, was gerade gilt, und weist eine veraltete
 * Liste ab, statt still zu entfernen, was ein anderer dazugetan hat
 * (Code-Prüfung E3, Befund 1; Kern, materialEingabe).
 */
export function grundlageListe(e: Pick<Entwurf, "grundlage">): string[] {
  return e.grundlage.split(",").filter(Boolean);
}

/** „Material ändern“: dieselbe Liste, gefüllt aus der freigegebenen – die Kennungen bleiben. */
export function aenderungAus(aufnahmeId: string, positionen: readonly PositionAmServer[]): Entwurf {
  return {
    fassung: FASSUNG,
    aufnahmeId,
    art: "aenderung",
    grundlage: geltendeGrundlage(positionen),
    positionen: geltendePositionen(positionen).map((p) => ({
      id: p.id,
      herkunft: p.herkunft,
      stationId: p.stationId,
      artikelnummer: p.artikelnummer,
      bezeichnung: p.bezeichnung,
      menge: mengeZeigen(p.menge),
      einheit: p.einheit,
      preis: p.preis,
    })),
  };
}

// --- Bearbeiten (jede Änderung gibt einen neuen Entwurf zurück) ---------------------------------

export function mengeSetzen(e: Entwurf, id: string, menge: string): Entwurf {
  return { ...e, positionen: e.positionen.map((p) => (p.id === id ? { ...p, menge } : p)) };
}

export function entfernen(e: Entwurf, id: string): Entwurf {
  return { ...e, positionen: e.positionen.filter((p) => p.id !== id) };
}

/** Fügt eine Position am Ende ihrer Gruppe an. */
export function dazu(e: Entwurf, p: EntwurfPosition): Entwurf {
  return { ...e, positionen: [...e.positionen, p] };
}

/** „Aus der Aufnahme neu übernehmen“: die Gruppe „Aus der Aufnahme“ frisch aus der Aufnahme – der Rest bleibt. */
export function aufnahmeNeuUebernehmen(e: Entwurf, aufnahme: AufnahmeLaengen, kennung: KennungsQuelle = neueKennung): Entwurf {
  return {
    ...e,
    grundlage: e.art === "freigabe" ? aufnahmeFingerabdruck(aufnahme) : e.grundlage,
    positionen: [...e.positionen.filter((p) => p.herkunft !== "aufnahme"), ...aufnahmePositionen(aufnahme, kennung)],
  };
}

/**
 * Stimmt die Gruppe „Aus der Aufnahme“ mit dem überein, was die Aufnahme
 * jetzt ergibt (gleiche Stationen, Bezeichnungen, Mengen – Längen wie
 * Material)? Nach der Freigabe zeigt die Seite sonst „Aus der Aufnahme
 * übernehmen“ – etwa nach einem Nachtrag, der eine Länge oder eine Menge
 * berichtigt hat.
 */
export function wieInDerAufnahme(e: Entwurf, aufnahme: AufnahmeLaengen): boolean {
  const zeile = (p: Pick<EntwurfPosition, "stationId" | "bezeichnung" | "menge">) => `${p.stationId ?? ""}:${p.bezeichnung}:${mengeLesen(p.menge) ?? p.menge}`;
  const jetzt = e.positionen
    .filter((p) => p.herkunft === "aufnahme")
    .map(zeile)
    .sort();
  const frisch = aufnahmePositionen(aufnahme, () => "")
    .map(zeile)
    .sort();
  return jetzt.length === frisch.length && jetzt.every((z, i) => z === frisch[i]);
}

/** Die Positionen in der Reihenfolge, in der sie angezeigt und gesendet werden: nach Gruppe, darin wie angefügt. */
export function geordnet(positionen: readonly EntwurfPosition[]): EntwurfPosition[] {
  const rang = (h: MaterialHerkunft) => GRUPPEN.findIndex((g) => g.herkunft === h);
  return positionen
    .map((p, i) => ({ p, i }))
    .sort((a, b) => rang(a.p.herkunft) - rang(b.p.herkunft) || a.i - b.i)
    .map((x) => x.p);
}

// --- Speichern im Browser ----------------------------------------------------------------------

/** Was vom localStorage gebraucht wird – in den Tests ein Ersatz. */
export interface Speicher {
  getItem(schluessel: string): string | null;
  setItem(schluessel: string, wert: string): void;
  removeItem(schluessel: string): void;
}

/** Der Schlüssel des Entwurfs vor der Freigabe. */
export function entwurfSchluessel(aufnahmeId: string): string {
  return `he-crm-entwurf-${aufnahmeId}`;
}

/** Der Schlüssel einer Änderung nach der Freigabe. */
export function aenderungSchluessel(aufnahmeId: string): string {
  return `he-crm-aenderung-${aufnahmeId}`;
}

const istText = (x: unknown): x is string => typeof x === "string";
const istTextOderNull = (x: unknown): x is string | null => x === null || typeof x === "string";

function istPosition(x: unknown): x is EntwurfPosition {
  if (!x || typeof x !== "object") return false;
  const p = x as Record<string, unknown>;
  return (
    istKennung(p["id"]) &&
    (MATERIAL_HERKUENFTE as readonly unknown[]).includes(p["herkunft"]) &&
    (p["stationId"] === null || istKennung(p["stationId"])) &&
    istTextOderNull(p["artikelnummer"]) &&
    istText(p["bezeichnung"]) &&
    istText(p["menge"]) &&
    istText(p["einheit"]) &&
    (p["preis"] === null || (typeof p["preis"] === "number" && Number.isFinite(p["preis"])))
  );
}

/**
 * Liest einen gespeicherten Entwurf. Alles, was nicht genau passt – kein
 * JSON, eine andere Besichtigung, eine andere Art oder Fassung, eine
 * kaputte Position, eine Kennung doppelt –, ergibt null: Dann zeigt die
 * Seite die Vorbelegung, nie einen halben Entwurf. Bei einer Änderung muss
 * auch die Grundlage stimmen (`grundlage`).
 */
export function entwurfLesen(roh: string | null, aufnahmeId: string, art: EntwurfArt, grundlage?: string): Entwurf | null {
  if (!roh) return null;
  let wert: unknown;
  try {
    wert = JSON.parse(roh);
  } catch {
    return null;
  }
  if (!wert || typeof wert !== "object") return null;
  const e = wert as Record<string, unknown>;
  if (e["fassung"] !== FASSUNG || e["aufnahmeId"] !== aufnahmeId || e["art"] !== art || !istText(e["grundlage"])) return null;
  const positionen = e["positionen"];
  if (!Array.isArray(positionen) || positionen.length > BUERO_GRENZEN.positionen || !positionen.every(istPosition)) return null;
  if (new Set(positionen.map((p) => p.id)).size !== positionen.length) return null;
  if (grundlage !== undefined && e["grundlage"] !== grundlage) return null;
  return { fassung: FASSUNG, aufnahmeId, art, grundlage: e["grundlage"], positionen };
}

/** Liest sicher – privater Modus, gesperrter Speicher: dann eben nichts. */
export function speicherLesen(speicher: Speicher | null, schluessel: string): string | null {
  try {
    return speicher?.getItem(schluessel) ?? null;
  } catch {
    return null;
  }
}

/** Schreibt sicher; gibt zurück, ob es ging (voller Speicher, privater Modus: nein). */
export function speicherSchreiben(speicher: Speicher | null, schluessel: string, wert: string | null): boolean {
  if (!speicher) return false;
  try {
    if (wert === null) speicher.removeItem(schluessel);
    else speicher.setItem(schluessel, wert);
    return true;
  } catch {
    return false;
  }
}

// --- Senden --------------------------------------------------------------------------------

/** Was an einer Position nicht stimmt – `id` null: an der ganzen Liste. */
export interface Mangel {
  id: string | null;
  meldung: string;
}

export type SendeBereit = { ok: true; eingabe: FreigabeEingabe } | { ok: false; maengel: Mangel[] };

/**
 * Macht aus dem Entwurf, was der Server bekommt – alle Positionen in der
 * angezeigten Reihenfolge, die Mengen als Zahl – und prüft es vorher mit
 * DEMSELBEN Vertrag wie der Server (Kern, freigabeEingabe). Was nicht
 * passt, steht je Position in `maengel`; dann wird nichts gesendet.
 */
export function zumSenden(e: Entwurf): SendeBereit {
  const liste = geordnet(e.positionen);
  const maengel: Mangel[] = [];
  const positionen = liste.map((p) => {
    const menge = mengeLesen(p.menge);
    if (menge === null) maengel.push({ id: p.id, meldung: "Bitte eine Menge eingeben, zum Beispiel 2 oder 12,5." });
    return {
      id: p.id,
      herkunft: p.herkunft,
      stationId: p.stationId,
      artikelnummer: p.artikelnummer,
      bezeichnung: p.bezeichnung,
      menge: menge ?? 0,
      einheit: p.einheit,
      preis: p.preis,
    };
  });
  if (maengel.length > 0) return { ok: false, maengel };

  const geprueft = freigabeEingabe.safeParse({ positionen });
  if (geprueft.success) return { ok: true, eingabe: geprueft.data };
  return {
    ok: false,
    maengel: geprueft.error.issues.map((f) => {
      const stelle = f.path[0] === "positionen" && typeof f.path[1] === "number" ? liste[f.path[1]] : undefined;
      return { id: stelle?.id ?? null, meldung: f.message };
    }),
  };
}

// --- Anzeige ---------------------------------------------------------------------------------

/** „Station 2 · Weg vom Dach zum Technikraum“ – woher eine Position aus der Aufnahme kommt. */
export function stationText(stationId: string | null, stationen: readonly { id: string; nr: number; art: string }[]): string | null {
  if (stationId === null) return null;
  const s = stationen.find((x) => x.id === stationId);
  if (!s) return null;
  const name = STATIONEN.find((x) => x.schluessel === s.art)?.name;
  return name ? `Station ${s.nr} · ${name}` : `Station ${s.nr}`;
}

/**
 * Woher eine Position kommt, für „aus: …“ – nur bei „Aus der Aufnahme“: die
 * Station, oder (seit E3b) „Material am Schluss“, wenn der Verkäufer es auf
 * der Seite „Material“ angab. Sonst null.
 */
export function woherText(p: Pick<EntwurfPosition, "herkunft" | "stationId">, stationen: readonly { id: string; nr: number; art: string }[]): string | null {
  if (p.herkunft !== "aufnahme") return null;
  return stationText(p.stationId, stationen) ?? "Material am Schluss";
}

/** Wie viele Positionen die Menge 0 haben – sie stehen so auf der Materialliste; die Seite sagt es vorher. */
export function mitMengeNull(e: Entwurf): number {
  return e.positionen.filter((p) => mengeLesen(p.menge) === 0).length;
}
